import { Box, Typography } from '@mui/material';
import HLCard from 'components/HLCard';
import PatientDetailsCard from 'components/PatientDetailsCard';
import NoDataForNow from 'pages/Dashboard/components/NoDataForNow';
import Tabs from 'pages/Dashboard/components/Tabs';
import UserAddedToolTip from 'pages/Dashboard/components/UserAddedToolTip';
import {
  ShowMoreDrawerChildProps,
  sliceForShowMore,
} from 'pages/Dashboard/ShowMoreDrawer';
import {
  GroupedSymptoms,
  NestedSymptoms,
  PatientSymptom,
} from 'pages/Dashboard/types/symptoms.types';
import * as React from 'react';

enum SymptomsTabLabels {
  WITHIN_30_DAYS = 'WITHIN 30 DAYS',
  PRIOR = 'PRIOR',
}

export interface SymptomsCardProps extends ShowMoreDrawerChildProps {
  gridSize?: number;
  reportedSymptoms?: PatientSymptom[],
  reportedSymptomsHistory?: PatientSymptom[],
  onShowMoreSymptoms?:(idx?: number) => void;
  drawerTabIndex?: number;
}

interface TabPanelsProps{
  groupedSymptom: GroupedSymptoms,
  value: number,
  index: number,
}

function SymptomsCardTabPanels(
  { groupedSymptom, value, index }: TabPanelsProps,
): JSX.Element | null {
  return (
    <Box hidden={value !== index}>
      { !Object.keys(groupedSymptom).length ? <NoDataForNow />
        : Object.keys(groupedSymptom).map((categoryKey) => (
          <React.Fragment key={`health-record-${categoryKey}`}>
            <Typography variant='body1' color='text.primary' my={1}>
              {categoryKey}
            </Typography>
            {groupedSymptom[categoryKey].map((data, idx) => (
              <Box key={data.symptom.name} display='flex' alignItems='center' justifyContent='space-between' mb={0.5}>
                <Box display='flex' alignItems='center'>
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'text.secondary',
                      mr: 1,
                    }}
                  />
                  <Typography variant='body2' color='text.secondary'>
                    {data.symptom.name}
                  </Typography>
                </Box>
                <UserAddedToolTip source={data.source} />
              </Box>
            ))}
          </React.Fragment>
        ))}
    </Box>
  );
}

export default function SymptomsCard({
  gridSize,
  drawerMode = false,
  onShowMoreSymptoms = () => null,
  drawerTabIndex,
  reportedSymptoms = [],
  reportedSymptomsHistory = [],
}: SymptomsCardProps): JSX.Element | null {
  const [value, setValue] = React.useState(0);

  const groupSymptomsByCategory = (slicedSymptoms: PatientSymptom[]) => {
    const symptomsData: GroupedSymptoms = {};

    slicedSymptoms.forEach((data) => {
      const {
        symptom: { category },
      } = data;
      const requiredData: NestedSymptoms = {
        symptom: data.symptom,
        startDate: data.startDate,
        source: data.source,
      };

      if (Object.hasOwn(symptomsData, category)) {
        symptomsData[category] = [...symptomsData[category], requiredData];
      }
      else {
        symptomsData[category] = [requiredData];
      }
    });
    return symptomsData;
  };

  const withinMonthSymptoms = reportedSymptoms.sort(
    (a, b) => a.symptom.category.localeCompare(b.symptom.category),
  );

  const priorSymptoms = reportedSymptomsHistory.sort(
    (a, b) => a.symptom.category.localeCompare(b.symptom.category),
  );

  const withinMonthSlice = sliceForShowMore(withinMonthSymptoms, !drawerMode);
  const priorSlice = sliceForShowMore(priorSymptoms, !drawerMode);

  const withinMonthGrpSymptoms = groupSymptomsByCategory(withinMonthSlice);
  const priorGrpSymptoms = groupSymptomsByCategory(priorSlice);
  React.useEffect(() => {
    if (drawerMode) { setValue(drawerTabIndex || 0); }
  }, [drawerTabIndex, drawerMode]);
  const symptomsPresent = reportedSymptoms.length > 0 || reportedSymptomsHistory.length > 0;
  const handleChangeTab = (tab:number) => {
    setValue(tab);
  };

  const content = (
    <>
      <Box mb={2}>
        <Tabs
          tabItems={[
            {
              label: `${SymptomsTabLabels.WITHIN_30_DAYS} (${reportedSymptoms.length})`,
              value: 0,
            },
            { label: `${SymptomsTabLabels.PRIOR} (${reportedSymptomsHistory.length})`, value: 1 },
          ]}
          selectedTab={value}
          setSelectedTab={(tab) => handleChangeTab(tab)}
        />
      </Box>
      <HLCard sx={{ p: drawerMode ? 1.5 : 0, border: 'none' }}>
        <SymptomsCardTabPanels groupedSymptom={withinMonthGrpSymptoms} value={value} index={0} />
        <SymptomsCardTabPanels groupedSymptom={priorGrpSymptoms} value={value} index={1} />
      </HLCard>
    </>
  );

  if (drawerMode) {
    return content;
  }

  return (
    <PatientDetailsCard
      title='Symptoms'
      showHeader={!drawerMode && symptomsPresent}
      gridProps={{ xs: 12, md: !drawerMode ? gridSize : undefined }}
      key='health-record-reported-condition'
      viewDetailsBtnLabel='View all'
      showViewDetails={
        symptomsPresent
        && !drawerMode
        && (value === 0 ? withinMonthSymptoms.length : priorSymptoms.length) > 3
      }
      onViewDetails={() => onShowMoreSymptoms(value)}
      showSubHeader={false}
    >
      {content}
    </PatientDetailsCard>
  );
}
